import { Box3, Camera, Matrix4, Plane, Points, Raycaster, Vector2, Vector3 } from 'three';
import { INTERSECTED, NOT_INTERSECTED } from 'three-mesh-bvh';

import { isIdentityMatrix4, isSameVector2 } from '../math';
import { RawObject } from './RawObject';
import { Sight } from './Sight';

const NORMAL_Z = new Vector3(0, 0, -1);
const _point = new Vector3();

export class PointPicker {
  public raycaster: Raycaster;

  private points: Array<Points>;
  private camera: Camera;

  /** 可视范围 */
  private visibleZ?: [number, number];
  /** NDC 标准坐标 */
  private ndcPoint: Vector2;
  /** 拟合地面 */
  private ground: {
    x: number;
    y: number;
    plane: Plane;
  };

  /** 此处存储的值是范围内的点坐标和距离值，例如 [x0,y0,z0,distance0, x1,y1,z1,distance1,...] */
  private collection: Array<number>;
  private inverseMatrix: Matrix4;

  // 在点云比较稀疏的时候，如果 threshold 比较大的话，会出现引导点在鼠标附近乱跳的情况，所以此处把 threshold 缩小
  constructor(target: RawObject, camera: Camera, threshold = 0.05) {
    this.points = [];
    this.setPoints(target);

    this.camera = camera;
    this.raycaster = new Raycaster();
    this.ndcPoint = new Vector2();

    this.ground = {
      x: 0,
      y: 0,
      plane: new Plane(),
    };
    this.setGroundPlaneZ(0);

    this.inverseMatrix = new Matrix4();
    this.collection = [];

    // 将射线图层调整至文件图层
    this.raycaster.layers.set(Sight.RAW_OBJECT_LAYER);

    if (this.raycaster.params.Points) {
      this.raycaster.params.Points.threshold = threshold;
    } else {
      this.raycaster.params.Points = { threshold };
    }
  }

  /**
   * 设置待选取的点云对象
   * @param target
   */
  public setPoints(target: RawObject) {
    this.points = [];

    target.object.traverse((obj) => {
      if (obj instanceof Points) {
        this.points.push(obj);

        if (
          typeof obj.material.uniforms?.minZ?.value === 'number' &&
          typeof obj.material.uniforms?.maxZ?.value === 'number'
        ) {
          this.visibleZ = [obj.material.uniforms?.minZ?.value, obj.material.uniforms?.maxZ?.value];
        }
      }
    });
  }

  /**
   * 根据屏幕上点的坐标，找到一个距离最近的点
   * @param point 屏幕点坐标，ndc 坐标系
   * @param result 返回点坐标
   * @param isGroundAdsorb 是否吸附到地面
   * @param isPCDAdsorb 是否吸附到点云（当且仅当 isGroundAdsorb === true 时有效）
   * @returns
   */
  public findNearestPoint(point: Vector2 | Vector3, result = new Vector3(), isGroundAdsorb = true, isPCDAdsorb = true) {
    this.ndcPoint.set(point.x, point.y);
    // 更新射线并获取点集
    this.raycaster.setFromCamera(this.ndcPoint, this.camera);
    this.collection = this.getPointsByRay(this.raycaster) ?? [];

    // 如果不需要吸附地面，则返回点集中离鼠标最近的点, 此时 isPCDAdsorb 值无效
    if (!isGroundAdsorb) {
      return this.getNearest((x, y, z, dis) => dis, result);
    }

    // TODO: 适配已分割过地面点云的数据 ( with geometry.attributes.ground ）

    const positions = this.estimateGroundPointIndexs(this.collection, 4, this.visibleZ);
    if (positions.length === 0) return isPCDAdsorb ? null : this.getPointInPlane(point, result);

    // 取地点z值的平均值作为地面高度
    const avgZ = positions.reduce((prev, id) => prev + this.collection[id + 2], 0) / positions.length;
    // 设置地平面
    this.setGroundPlaneZ(avgZ);

    // 计算地平面与射线的交点，作为基准点
    const avgPoint =
      this.getPointInPlane(point) ??
      new Vector3(this.collection[positions[0]], this.collection[positions[0] + 1], avgZ);

    // 获取离基准点最近的点
    const res = this.getNearest((x, y, z) => _point.set(x, y, z).distanceToSquared(avgPoint), result);

    res && this.setGroundPlane(res.x, res.y);

    return res;
  }

  /**
   * 以当前点来更新地面高度
   * @param point
   * @returns
   */
  public updateGroundByPoint(point: Vector3) {
    // 如果传入点坐标的(x, y)值与当前地平面坐标的(x, y)值相同，则直接返回
    if (isSameVector2(this.ground, point, 1e-4)) {
      return this.ground.plane.constant;
    }

    // 射线从 点正上方出发，射向 Z轴负方向
    this.raycaster.set(_point.set(point.x, point.y, point.z + 10), NORMAL_Z);
    this.collection = this.getPointsByRay(this.raycaster) ?? [];

    const positions = this.estimateGroundPointIndexs(this.collection, 4, this.visibleZ);

    // 如果附近无任何点云，则不改变当前地平面，并返回
    if (positions.length === 0) {
      this.setGroundPlane(point.x, point.y);

      return this.ground.plane.constant;
    }

    // 取地点z值的平均值作为地面高度
    const sumZ = positions.reduce((prev, id) => prev + this.collection[id + 2], 0);
    // 设置地平面
    this.setGroundPlane(point.x, point.y, sumZ / positions.length);

    return this.ground.plane.constant;
  }

  public setGroundPlaneZ(z: number) {
    this.ground.plane.set(NORMAL_Z, z);
  }

  public setGroundPlane(x: number, y: number, z?: number) {
    this.ground.x = x;
    this.ground.y = y;
    if (z) {
      this.setGroundPlaneZ(z);
    }
  }

  public getPointInPlane(ndc: Vector2 | Vector3, result?: Vector3) {
    this.ndcPoint.set(ndc.x, ndc.y);
    // 更新射线并获取点集
    this.raycaster.setFromCamera(this.ndcPoint, this.camera);

    return this.raycaster.ray.intersectPlane(this.ground.plane, result ?? new Vector3());
  }

  /**
   * 获取射线阈值范围内的点集
   * @param point point 屏幕上点的坐标 ndc
   * @returns
   */
  private getPointsByRay(rayLine: Raycaster) {
    if (this.points.length === 0) return undefined;

    const pointCollection: Array<number> = [];
    // 如果已经计算好 BVH 就用 BVH，不然采用回退的算法
    if (this.points[this.points.length - 1].geometry.boundsTree) {
      for (let i = 0, l = this.points.length; i < l; i++) {
        const point = this.points[i];
        const localThreshold =
          (rayLine.params.Points?.threshold ?? 1) / ((point.scale.x + point.scale.y + point.scale.z) / 3);
        const localThresholdSq = localThreshold * localThreshold;

        const { ray } = rayLine;
        if (!isIdentityMatrix4(point.matrixWorld)) {
          this.inverseMatrix.copy(point.matrixWorld).invert();
          ray.applyMatrix4(this.inverseMatrix);
        }

        point.geometry.boundsTree?.shapecast({
          boundsTraverseOrder: (box: Box3) => {
            return box.distanceToPoint(ray.origin);
          },
          intersectsBounds: (box, isLeaf, score) => {
            box.expandByScalar(localThreshold);
            return ray.intersectsBox(box) ? INTERSECTED : NOT_INTERSECTED;
          },
          intersectsTriangle: (triangle) => {
            const distancesToRaySq = ray.distanceSqToPoint(triangle.a);
            if (distancesToRaySq < localThresholdSq) {
              const distanceToPoint = ray.origin.distanceTo(triangle.a);
              _point.copy(triangle.a).applyMatrix4(point.matrixWorld);
              pointCollection.push(..._point.toArray(), distanceToPoint);
            }
          },
        });
      }
    } else {
      const intersects = rayLine.intersectObjects(this.points, false);

      intersects.forEach(({ distance, index, object }) => {
        if (typeof index === 'number') {
          const position = (object as Points).geometry.attributes.position;
          pointCollection.push(position.getX(index), position.getY(index), position.getZ(index), distance);
        }
      });
    }
    return pointCollection;
  }

  /**
   * 从点集数据中预估分割地面点（适用场景：点云的地面点密集度远大于非地面点）：
   * 1. 将所有点根据 z 坐标值分桶，桶大小为 0.01
   * 2. 按桶内元素个数排序
   * 3. 取前 1/2 名的桶内元素作为地点，并返回索引
   * @param pointCollection 点集数组
   * @param itemSize 单个点的数据长度：3 => x, y, z | 4 => x, y, z, distance
   * @param visibleZ 可视范围
   * @returns 地面点在传入的点集中的索引数组
   */
  private estimateGroundPointIndexs(pointCollection: Array<number>, itemSize: number, visibleZ?: [number, number]) {
    let positionMap = new Map<string, number[]>();

    for (let i = 0; i < pointCollection.length; i += itemSize) {
      const pointZ = pointCollection[i + 2];
      if (!visibleZ || (pointZ >= visibleZ[0] && pointZ <= visibleZ[1])) {
        // 点的位置目前只保留两位小数，用来保存
        const num = pointZ.toFixed(2);
        let currentPositions = positionMap.get(num);
        if (!currentPositions) {
          currentPositions = [];
          positionMap.set(num, currentPositions);
        }
        currentPositions.push(i);
      }
    }

    if (positionMap.size === 0) return [];

    // 按z值出现频率排序
    const positionArray = Array.from(positionMap, ([z, indexs]) => ({ z: parseFloat(z), indexs })).sort(
      (a, b) => b.indexs.length - a.indexs.length
    );

    // 若前两名的z值相差不超过 0.02（已做过地面分割的点云 z 值差距通常不超过 0.02），则均被视为地点；否则只取第一名的点集作为地点序号
    const positions =
      positionArray[1] && Math.abs(positionArray[1].z - positionArray[0].z) <= 0.02
        ? positionArray[0].indexs.concat(positionArray[1].indexs)
        : positionArray[0].indexs;

    return positions;
  }

  /**
   * 获取点集数据中距离最近的点
   * @param evalFunc 距离评价函数，返回数值越小 => 距离越近
   * @returns
   */
  private getNearest(evalFunc: (x: number, y: number, z: number, dis: number) => number, result?: Vector3) {
    if (this.collection.length === 0) return null;

    let res = result ?? new Vector3();
    let closestDistance = Number.MAX_SAFE_INTEGER;

    // 查找与基准点最近邻的点来吸附
    for (let i = 0; i < this.collection.length; i += 4) {
      const pointZ = this.collection[i + 2];
      if (!this.visibleZ || (pointZ >= this.visibleZ[0] && pointZ <= this.visibleZ[1])) {
        const distanceSquared = evalFunc(
          this.collection[i],
          this.collection[i + 1],
          this.collection[i + 2],
          this.collection[i + 3]
        );
        if (distanceSquared < closestDistance) {
          closestDistance = distanceSquared;
          res.fromArray(this.collection, i);
        }
      }
    }

    return res;
  }
}
